import { auth } from '../firebase';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * List groups of duplicate franchisee records
 * @returns {Promise<object>} - The data payload: { groups: [...] }
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated
 */
export const listFranchiseeDuplicates = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/franchisee-duplicates/list`;

    console.log("Fetching franchisee duplicates list:", { url });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to list franchisee duplicates');
    }

    return responseData.data;
  } catch (error) {
    console.error("Error listing franchisee duplicates:", error);
    throw error;
  }
};

/**
 * Merge one franchisee record into another (keep one, merge & suspend the other)
 * @param {string} keepId - The ID of the franchisee record to retain
 * @param {string} mergeId - The ID of the franchisee record to merge in and suspend
 * @returns {Promise<object>} - The merge result data
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated
 */
export const mergeFranchisees = async (keepId, mergeId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  if (!keepId || !mergeId) {
    throw new Error("Both keepId and mergeId are required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/franchisee-duplicates/merge`;

    console.log("Merging franchisees:", { url, keepId, mergeId });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ keepId, mergeId }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to merge franchisees');
    }

    return responseData.data;
  } catch (error) {
    console.error("Error merging franchisees:", error);
    throw error;
  }
};

/**
 * Preview the effect of merging one franchisee record into another (read-only; no writes).
 * @param {string} keepId - The ID of the franchisee record that would be retained
 * @param {string} mergeId - The ID of the franchisee record that would be merged in and suspended
 * @returns {Promise<object>} - The preview data payload
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated
 */
export const previewMergeFranchisees = async (keepId, mergeId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  if (!keepId || !mergeId) {
    throw new Error("Both keepId and mergeId are required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/franchisee-duplicates/preview-merge`;

    console.log("Previewing franchisee merge:", { url, keepId, mergeId });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ keepId, mergeId }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to preview franchisee merge');
    }

    return responseData.data;
  } catch (error) {
    console.error("Error previewing franchisee merge:", error);
    throw error;
  }
};

/**
 * Re-scan for duplicate franchisees and email admins with the results
 * @returns {Promise<object>} - The scan result data: { groupCount, emailed }
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated
 */
export const scanAndAlertFranchiseeDuplicates = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/franchisee-duplicates/scan-and-alert`;

    console.log("Scanning and alerting franchisee duplicates:", { url });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to scan and alert franchisee duplicates');
    }

    return responseData.data;
  } catch (error) {
    console.error("Error scanning and alerting franchisee duplicates:", error);
    throw error;
  }
};
